import { Box } from "@chakra-ui/react";
import { useContext } from "react";

import { LayerControl, LayerControlHeader } from "@/components/layer-control";
import { RanaMap } from "@/lib/rana-map";
import { ProcessRunFormMapViewStateContext } from "@/stores/process-run-form-map-view-state-provider";
import { ProjectContext } from "@/stores/project-provider";

import {
  MAP_WRAPPER_PROPS,
  ProcessRunFormFileFeatureSelectorMap,
} from "./components/process-run-form-file-feature-selector-map";
import { ProcessRunFormFileFeatureSelectorSearch } from "./components/process-run-form-file-feature-selector-search";
import { useSelectedFile } from "./hooks/use-selected-file";
import { useSelectedFileOwn } from "./hooks/use-selected-file-own";
import { useSelectedFileStandard } from "./hooks/use-selected-file-standard";
import { FeatureSelectorFieldProps } from "./types/feature-selector-field-props-type";
import { featureSelectorGetMapId } from "./utils/feature-selector-get-map-id";

export function ProcessRunFormFileFeatureSelector(props: FeatureSelectorFieldProps) {
  const fileType = useSelectedFile(props);
  const project = useContext(ProjectContext);
  const { mapViewState, setMapView } = useContext(ProcessRunFormMapViewStateContext);

  if (fileType?.type === "standard") {
    return <ProcessRunFormFileStandard path={fileType.path} fieldProps={props} />;
  }

  if (fileType?.type === "own") {
    return <ProcessRunFormFileOwn id={fileType.id} fieldProps={props} />;
  }

  // No file selected yet (or the form is still loading): show an empty map centered on the
  // project location as a placeholder until the user picks a file.
  // 1. The user has not yet selected a file (the source form field is empty)
  // 2. The form data or schema source config is not available yet (form still loading)
  // 3. The selected value does not resolve to a known standard boundary path or own-file ID
  // In practice this is most commonly seen while the user is filling in the form and
  // has not yet made a file selection. Show an empty map centered on the project location.
  const projectLocationViewState = project.location
    ? {
        longitude: project.location.coordinates[0],
        latitude: project.location.coordinates[1],
        zoom: 11,
      }
    : undefined;

  const initialViewState = mapViewState ?? projectLocationViewState;

  return (
    <Box {...MAP_WRAPPER_PROPS}>
      <RanaMap
        bounds={null}
        mapProps={{
          initialViewState,
          onMoveEnd: setMapView,
        }}
        deckGlProps={{}}
        mapId={featureSelectorGetMapId(props, "empty_map")}
      />
    </Box>
  );
}

// Standard boundary's (so buurten and gemeenten)
const ProcessRunFormFileStandard = ({
  path,
  fieldProps,
}: {
  path: string;
  fieldProps: FeatureSelectorFieldProps;
}) => {
  const { layer, layerName, isLoading, bounds, pmTilesUrl, preferProjectLocation } =
    useSelectedFileStandard(fieldProps, path);

  return (
    <ProcessRunFormFileFeatureSelectorMap
      layerNames={[layerName]}
      layers={layer ? [layer] : []}
      isLoading={isLoading}
      bounds={bounds}
      preferProjectLocation={preferProjectLocation}
      fieldProps={fieldProps}
      mapSuffix="file_standard"
    >
      {pmTilesUrl && (
        <RanaMap.ControlsTopLeft>
          <ProcessRunFormFileFeatureSelectorSearch
            fieldProps={fieldProps}
            bounds={bounds}
            pmTilesUrl={pmTilesUrl}
            layerName={layerName}
          />
        </RanaMap.ControlsTopLeft>
      )}
    </ProcessRunFormFileFeatureSelectorMap>
  );
};

// Own files (so any files in your projects file directory)
const ProcessRunFormFileOwn = ({
  id,
  fieldProps,
}: {
  id: string;
  fieldProps: FeatureSelectorFieldProps;
}) => {
  const {
    descriptorId,
    layers,
    layerNames,
    isLoading,
    bounds,
    fileTitle,
    visibleLayers,
    setVisibleLayers,
  } = useSelectedFileOwn(id);

  return (
    <ProcessRunFormFileFeatureSelectorMap
      layerNames={layerNames}
      layers={layers}
      isLoading={isLoading}
      bounds={bounds}
      fieldProps={fieldProps}
      mapSuffix="file_own"
    >
      {layerNames.length > 1 && (
        <RanaMap.ControlsTopLeft>
          <LayerControlHeader />
          <LayerControl
            descriptorId={descriptorId}
            title={fileTitle}
            layers={layerNames}
            visibleLayers={visibleLayers}
            setVisibleLayers={setVisibleLayers}
          />
        </RanaMap.ControlsTopLeft>
      )}
    </ProcessRunFormFileFeatureSelectorMap>
  );
};
